import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import { useSnackbar } from 'material-ui-snackbar-provider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import { getSubGroupsByGroupIdThunk } from '../../../store/subGroups/subGroupsSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getGroupsThunk } from '../../../store/groups/groupsSlice';
import { SUBGROUP_LIST_ITEM, USER } from '../../../enums/TYPES';
import { ROLES, RolesLowerCase } from '../../../enums/ROLES';
import { displayRole } from '../../../helpers/displayRole';
import styles from './EditSubGroupModal.module.scss';
import { patchSubGroupAnalysts } from '../../../api';
import { NOTIFICATION } from '../../../enums/ENUMS';
import SearchInput from '../../modules/SearchInput';
import RolePlate from '../../modules/RolePlate';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TEditSubGroupModal = {
  open: boolean;
  switchModal: () => void;
  group?: SUBGROUP_LIST_ITEM;
  managementList?: USER[];
  viewersList?: USER[];
};

const EditSubGroupModal: React.FC<TEditSubGroupModal> = ({ open, switchModal, group, managementList = [], viewersList = [] }) => {
  const canViewEmails = useCanViewEmails();
  const [allAnalysts] = useState<USER[]>(managementList);
  const assignedAnalystsIds = useMemo(() => {
    return group?.analysts.map((analyst) => analyst.id);
  }, [group]);
  const [allViewers] = useState<USER[]>(viewersList);
  const assignedViewersIds = useMemo(() => {
    return group?.viewers.map((viewer) => viewer.id);
  }, [group]);
  const { sort, sortOption } = useAppSelector((state) => state.subGroupsStore);

  const currentRole = useAppSelector((state) => state.user.role);
  const notification = useSnackbar();
  const [newAnalysts, setNewAnalysts] = useState<USER[]>();
  const [newViewers, setNewViewers] = useState<USER[]>();
  const [previousStep, setPreviousStep] = useState(0);
  const [newTitle, setNewTitle] = useState<string>('');
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const toFourthStepButtonRef = useRef<HTMLDivElement>(null);
  const backToFirstStepButtonRef = useRef<HTMLDivElement>(null);
  const backToSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const backToThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [step, setStep] = useState(1);

  useEffect(() => {
    if (group) {
      setNewAnalysts(managementList);
      setNewViewers(viewersList);
    }
    if (currentRole === ROLES.ANALYST) {
      setStep(2);
    }
  }, [group, managementList, viewersList]);

  const [addedAnalysts, setAddedAnalysts] = useState<string[]>([]);
  const [removedAnalysts, setRemovedAnalysts] = useState<string[]>([]);
  const [addedViewers, setAddedViewers] = useState<string[]>([]);
  const [removedViewers, setRemovedViewers] = useState<string[]>([]);

  const [selectAllOa, setSelectAllOa] = useState(false);
  const [selectAllAnalyst, setSelectAllAnalyst] = useState(false);

  const oAuserChips = useMemo(() => {
    const currentUsers = newAnalysts?.filter((user) => user.role.title === RolesLowerCase.ORGANIZATION_ADMIN);
    const filteredUsers = currentUsers?.filter(
      (user) => (assignedAnalystsIds?.includes(user.id) && !removedAnalysts.includes(user.id)) || addedAnalysts.includes(user.id),
    );
    return filteredUsers?.map((user) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedAnalystsIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedAnalystsIds, newAnalysts, removedAnalysts, addedAnalysts]);

  const analystUserChips = useMemo(() => {
    const currentUsers = newAnalysts?.filter((user) => user.role.title === RolesLowerCase.ANALYST);
    const filteredUsers = currentUsers?.filter(
      (user) => (assignedAnalystsIds?.includes(user.id) && !removedAnalysts.includes(user.id)) || addedAnalysts.includes(user.id),
    );
    return filteredUsers?.map((user) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedAnalystsIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedAnalystsIds, newAnalysts, removedAnalysts, addedAnalysts]);

  const viewerUserChips = useMemo(() => {
    const currentUsers = newViewers?.filter(
      (user) =>
        (user.role.title === RolesLowerCase.VIEWER && assignedViewersIds?.includes(user.id) && !removedViewers.includes(user.id)) ||
        addedViewers.includes(user.id),
    );
    return currentUsers?.map((user) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedViewersIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'viewer',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'viewer',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedViewersIds, newViewers, removedViewers, addedViewers]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>, type: 'analyst' | 'viewer') => {
    if (type === 'analyst') {
      setNewAnalysts(
        allAnalysts?.filter(
          (analyst) =>
            analyst?.username?.toLowerCase().includes(e.target.value.toLowerCase()) ||
            analyst?.email?.toLowerCase().includes(e.target.value.toLowerCase()),
        ),
      );
    } else {
      setNewViewers(
        allViewers?.filter(
          (viewer) =>
            viewer?.username?.toLowerCase().includes(e.target.value.toLowerCase()) ||
            viewer?.email?.toLowerCase().includes(e.target.value.toLowerCase()),
        ),
      );
    }
  };

  const removeUser = (e: any) => {
    if (e.target.name === 'analyst') {
      const alreadyUnchecked = removedAnalysts.includes(e.target.id);
      if (alreadyUnchecked) {
        setRemovedAnalysts(removedAnalysts.filter((id) => id !== e.target.id));
      } else {
        setRemovedAnalysts([...removedAnalysts, e.target.id]);
      }
    }
    if (e.target.name === 'viewer') {
      const alreadyUnchecked = removedViewers.includes(e.target.id);
      if (alreadyUnchecked) {
        setRemovedViewers(removedViewers.filter((id) => id !== e.target.id));
      } else {
        setRemovedViewers([...removedViewers, e.target.id]);
      }
    }
  };

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>, role: RolesLowerCase) => {
    if (role === RolesLowerCase.ORGANIZATION_ADMIN) {
      const oasIds = newAnalysts?.filter((oa) => oa.role.title === RolesLowerCase.ORGANIZATION_ADMIN).map((oa) => oa.id) || [];

      if (!selectAllOa) {
        setAddedAnalysts([...addedAnalysts, ...oasIds]);
      } else {
        setAddedAnalysts([...addedAnalysts.filter((oa) => !oasIds.includes(oa))]);
        setRemovedAnalysts([...removedAnalysts, ...oasIds]);
      }
      setSelectAllOa(!selectAllOa);
    } else if (role === RolesLowerCase.ANALYST) {
      const analystsIds =
        newAnalysts?.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).map((analyst) => analyst.id) || [];

      if (!selectAllAnalyst) {
        setAddedAnalysts([...addedAnalysts, ...analystsIds]);
      } else {
        setAddedAnalysts([...addedAnalysts.filter((analyst) => !analystsIds.includes(analyst))]);
        setRemovedAnalysts([...removedAnalysts, ...analystsIds]);
      }
      setSelectAllAnalyst(!selectAllAnalyst);
    } else if (role === RolesLowerCase.VIEWER) {
      const viewersIds = newViewers?.map((viewer) => viewer.id) || [];

      if (addedViewers.length !== newViewers?.length) {
        setAddedViewers([...addedViewers, ...viewersIds]);
      } else {
        setAddedViewers([]);
        setRemovedViewers(viewersIds);
      }
    }
  };

  const addUser = (e: any) => {
    if (e.target.name === 'analyst') {
      const alreadyChecked = addedAnalysts.includes(e.target.id);
      if (alreadyChecked) {
        setAddedAnalysts(addedAnalysts.filter((id) => id !== e.target.id));
      } else {
        setAddedAnalysts([...addedAnalysts, e.target.id]);
      }
    }

    if (e.target.name === 'viewer') {
      const alreadyChecked = addedViewers.includes(e.target.id);
      if (alreadyChecked) {
        setAddedViewers(addedViewers.filter((id) => id !== e.target.id));
      } else {
        setAddedViewers([...addedViewers, e.target.id]);
      }
    }
  };

  const onModalClose = () => {
    switchModal();
    setAddedAnalysts([]);
    setRemovedAnalysts([]);
    setAddedViewers([]);
    setRemovedViewers([]);
    setNewTitle('');
    if (currentRole === ROLES.ANALYST) {
      setStep(3);
    } else {
      setStep(1);
    }
  };

  const nextStep = () => {
    setPreviousStep(step);
    newViewers?.length ? setStep(4) : onSubmit();
  };

  const stepToAnalyst = () => {
    setPreviousStep(step);
    newAnalysts?.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).length ? setStep(3) : nextStep();
  };

  const toOaStep = () => {
    setPreviousStep(step);
    setStep(2);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setNewTitle(e.target.value);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await patchSubGroupAnalysts(group!.id, {
        title: newTitle.trim() || group!.title,
        usersToAdd: [...addedAnalysts, ...addedViewers],
        usersToRemove: [...removedAnalysts, ...removedViewers],
        groupId: location.state as string,
      });
      notification.showMessage(NOTIFICATION.UPDATED);
      dispatch(getSubGroupsByGroupIdThunk({ groupId: location.state as string, sort, sortOption }));
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      dispatch(getGroupsThunk());
      setPreviousStep(step);
      onModalClose();
      setLoading(false);
    }
  };

  const stepBack = () => {
    if (previousStep) {
      setStep(previousStep);
      setPreviousStep(0);
    } else if (currentRole === ROLES.ANALYST ? 3 : 1) {
      onModalClose();
    } else {
      setStep(1);
    }
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        toThirdStepButtonRef?.current?.click();
        break;
      case 3:
        toFourthStepButtonRef?.current?.click();
        break;
      case 4:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  useHotkeys('Backspace', () => {
    if (!open || loading) return;

    switch (step) {
      case 2:
        backToFirstStepButtonRef?.current?.click();
        break;
      case 3:
        backToSecondStepButtonRef?.current?.click();
        break;
      case 4:
        backToThirdStepButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={onModalClose} loading={loading}>
      {step === 1 && (
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            Edit Subgroup Name
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <input className={styles.field} defaultValue={group?.title} onChange={handleTitleChange} type="text" />
          <div className={styles.actionBtns}>
            <ContainedButton onClick={toOaStep} disabled={loading}>
              <div ref={toSecondStepButtonRef}>Next</div>
            </ContainedButton>
            <OutlinedButton kind="default" onClick={onModalClose}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <div className={styles.modalTitle}>
            Edit Organisation Admins List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.searchInput}>
              <SearchInput className={styles.input} onChange={(e) => onSearch(e, 'analyst')} />
            </div>
            <div className={styles.userChips}>{oAuserChips}</div>
            <div className={styles.usersList}>
              <label htmlFor="selectAllOa">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <div className={styles.rightSide}>
                    <input
                      id="selectAllOa"
                      onChange={(e) => onSelectAll(e, RolesLowerCase.ORGANIZATION_ADMIN)}
                      type="checkbox"
                      checked={selectAllOa}
                    />
                  </div>
                </div>
              </label>
              {newAnalysts
                ?.filter((user) => user.role.title === 'Organization Admin')
                .map((person) => (
                  <label htmlFor={person.id} key={person.id}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={person?.avatar_url} />
                        <div className={styles.info}>
                          <div className={styles.name}>{person?.username}</div>
                          {canViewEmails && (<Tooltip title={person.email}>
                            <div className={styles.email}>{person.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.rightSide}>
                        <RolePlate userRole={displayRole(person.role.title)} className={styles.rolePlate} />
                        <input
                          onChange={assignedAnalystsIds?.includes(person.id) ? removeUser : addUser}
                          name="analyst"
                          id={person.id}
                          type="checkbox"
                          // defaultChecked={assignedAnalystsIds?.includes(person.id)}
                          checked={
                            (assignedAnalystsIds?.includes(person.id) && !removedAnalysts.includes(person.id)) ||
                            addedAnalysts.includes(person.id)
                          }
                        />
                      </div>
                    </div>
                  </label>
                ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={stepToAnalyst} disabled={loading}>
                <div ref={toThirdStepButtonRef}>Next</div>
              </ContainedButton>
              <OutlinedButton kind="default" onClick={stepBack}>
                <div ref={backToFirstStepButtonRef}>Back</div>
              </OutlinedButton>
            </div>
          </div>
        </>
      )}
      {step === 3 && (
        <>
          <div className={styles.modalTitle}>
            Edit Analysts List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.searchInput}>
              <SearchInput className={styles.input} onChange={(e) => onSearch(e, 'analyst')} />
            </div>
            <div className={styles.userChips}>{analystUserChips}</div>
            <div className={styles.usersList}>
              <label htmlFor="selectAllAnalysts">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <div className={styles.rightSide}>
                    <input
                      id="selectAllAnalysts"
                      onChange={(e) => onSelectAll(e, RolesLowerCase.ANALYST)}
                      type="checkbox"
                      checked={selectAllAnalyst}
                    />
                  </div>
                </div>
              </label>
              {newAnalysts
                ?.filter((user) => user.role.title === RolesLowerCase.ANALYST)
                .map((person) => (
                  <label htmlFor={person.id} key={person.id}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={person?.avatar_url} />
                        <div className={styles.info}>
                          <div className={styles.name}>{person?.username}</div>
                          {canViewEmails && (<Tooltip title={person.email}>
                            <div className={styles.email}>{person.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.rightSide}>
                        <RolePlate userRole={displayRole(person.role.title)} className={styles.rolePlate} />
                        <input
                          onChange={assignedAnalystsIds?.includes(person.id) ? removeUser : addUser}
                          name="analyst"
                          id={person.id}
                          type="checkbox"
                          // defaultChecked={assignedAnalystsIds?.includes(person.id)}
                          checked={
                            (assignedAnalystsIds?.includes(person.id) && !removedAnalysts.includes(person.id)) ||
                            addedAnalysts.includes(person.id)
                          }
                        />
                      </div>
                    </div>
                  </label>
                ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={nextStep} disabled={loading}>
                <div ref={toFourthStepButtonRef}>Next</div>
              </ContainedButton>
              <OutlinedButton kind="default" onClick={stepBack}>
                <div ref={backToSecondStepButtonRef}></div>Back
              </OutlinedButton>
            </div>
          </div>
        </>
      )}
      {step === 4 && (
        <>
          <div className={styles.modalTitle}>
            Edit Viewers List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.searchInput}>
              <SearchInput className={styles.input} onChange={(e) => onSearch(e, 'viewer')} />
            </div>
            <div className={styles.userChips}>{viewerUserChips}</div>
            <div className={styles.usersList}>
              <label htmlFor="selectAllViewers">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <div className={styles.rightSide}>
                    <input
                      id="selectAllViewers"
                      onChange={(e) => onSelectAll(e, RolesLowerCase.VIEWER)}
                      type="checkbox"
                      defaultChecked={false}
                    />
                  </div>
                </div>
              </label>
              {newViewers?.map((person) => (
                <label htmlFor={person.id} key={person.id}>
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <Avatar avatar={person?.avatar_url} />
                      <div className={styles.info}>
                        <div className={styles.name}>{person?.username}</div>
                        {canViewEmails && (<Tooltip title={person.email}>
                          <div className={styles.email}>{person.email}</div>
                        </Tooltip>)}
                      </div>
                    </div>
                    <div className={styles.rightSide}>
                      <RolePlate userRole={displayRole(person.role.title)} className={styles.rolePlate} />
                      <input
                        onChange={assignedViewersIds?.includes(person.id) ? removeUser : addUser}
                        name="viewer"
                        id={person.id}
                        type="checkbox"
                        // defaultChecked={assignedViewersIds?.includes(person.id)}
                        checked={
                          (assignedViewersIds?.includes(person.id) && !removedViewers.includes(person.id)) ||
                          addedViewers.includes(person.id)
                        }
                      />
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={onSubmit} disabled={loading}>
                <div ref={submitButtonRef}>Update</div>
              </ContainedButton>
              <OutlinedButton kind="default" onClick={stepBack}>
                <div ref={backToThirdStepButtonRef}>Back</div>
              </OutlinedButton>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditSubGroupModal;
